import type { ColumnDef } from '@tanstack/react-table';
import type { RestrictionEvent } from 'contracts/restricted-products/restricted-products';
import { DataTableHelper, Formatter } from 'utils';

import {
  EventAction,
  EventJustificationStatus,
  EventActionsMenu,
} from 'pages/restricted-products/shared-components';

const dt = new DataTableHelper<RestrictionEvent>({
  enableHiding: false,
  enableSorting: false,
});

export const useRestrictedProductColumns =
  (): ColumnDef<RestrictionEvent>[] => {
    const id = dt.buildColumn({
      id: 'id',
      title: 'Id',
      accessorKey: 'id',
      size: 40,
      content: (row) => row.id,
    });

    const plant = dt.buildColumn({
      id: 'plant',
      title: 'Planta',
      accessorKey: 'plant',
      size: 40,
      content: (row) => row.plant,
    });

    const material = dt.buildColumn({
      id: 'material',
      title: 'Material',
      accessorKey: 'material',
      size: 40,
      content: (row) => row.material,
    });

    const materialDesc = dt.buildColumn({
      id: 'materialDesc',
      title: 'Desc. material',
      size: 200,
      accessorFn: (row) => row.materialDescription,
      content: (row) => row.materialDescription,
    });

    const materialQuant = dt.buildColumn({
      id: 'materialQuant',
      title: 'Quant.',
      size: 40,
      accessorFn: (row) => row.quantity,
      content: (row) => {
        const { quantity, materialUnit } = row;
        return [quantity, materialUnit].join(' ').toUpperCase();
      },
    });

    const saleMovementQuant = dt.buildColumn({
      id: 'saleMovementQuantity',
      title: 'Quant. venda',
      size: 40,
      accessorFn: (row) => row.saleMovementQuantity,
      content: (row) => {
        if (!row.resMovementType) return '---';
        const { saleMovementQuantity, saleMovementQuantityUnit } = row;
        return [saleMovementQuantity, saleMovementQuantityUnit].join(' ').toUpperCase();
      },
    });

    const batch = dt.buildColumn({
      id: 'batch',
      title: 'Lote',
      accessorKey: 'batch',
      size: 40,
      content: (row) => row.batch,
    });

    const action = dt.buildColumn({
      id: 'action',
      title: 'Ação',
      accessorKey: 'action',
      size: 220,
      content: (row) => <EventAction restrictionEvent={row} />,
    });

    const eventDate = dt.buildColumn({
      id: 'eventDate',
      title: 'Data',
      accessorKey: 'createdAt',
      size: 120,
      content: (row) => Formatter.date(row.createdAt, {
        format: 'dd/MM/yyyy HH:mm',
      }),
    });

    const justificationStatus = dt.buildColumn({
      id: 'justificationStatus',
      title: 'Status',
      accessorFn: (row) => row.justification,
      content: (row) => <EventJustificationStatus justification={row.justification} />,
    });

    const actionsMenu = dt.buildColumn({
      id: 'actionsMenu',
      title: '',
      content: (row) => {
        return (
          <div className={'flex justify-end'}>
            <EventActionsMenu event={row} />
          </div>
        );
      },
    });

    return [
      id,
      plant,
      material,
      materialDesc,
      materialQuant,
      saleMovementQuant,
      batch,
      action,
      eventDate,
      justificationStatus,
      actionsMenu,
    ];
  };
